import { TbBolt, TbHeadset, TbLine, TbShieldBolt } from 'react-icons/tb';

import { CreditUsageType, CreditUsageTypeCosts, DEFAULT_CREDIT_AMOUNT, NEW_USER_CREDIT_AMOUNT } from 'models';

type FeatureValue = boolean | string;

type Feature = {
    name: string;
    free: FeatureValue;
    plus: FeatureValue;
    tooltip?: string;
    highlight?: boolean;
};

type FeatureGroup = {
    category: string;
    features: Feature[];
};

type SupportSizing = {
    credits: number;
    description: string;
    unit: string;
};

type CreditService = {
    name: string;
    creditsPerUse: number | null;
    icon: typeof TbBolt;
    description: string;
    sizing?: SupportSizing[];
};

export const SUBSCRIPTION_FEATURES: readonly FeatureGroup[] = [
    {
        category: 'Usage',
        features: [
            {
                name: 'Team members',
                free: 'Unlimited',
                plus: 'Unlimited',
            },
            {
                name: 'Create and save designs',
                free: 'Up to 3 designs',
                plus: 'Unlimited',
            },
            {
                name: 'Components on design',
                free: '1 credit / component',
                plus: 'Unlimited',
            },
            {
                name: 'Link products to designs',
                free: true,
                plus: true,
            },
            {
                name: 'Browse reference design templates',
                free: true,
                plus: true,
            },
            {
                name: 'Annotate designs (groups and text)',
                free: true,
                plus: true,
            },
            {
                name: 'Add images and logos to designs',
                free: 'Up to 1 image',
                plus: 'Unlimited',
            },
            {
                name: 'Attach project files to designs',
                free: false,
                plus: true,
            },
        ],
    },
    {
        category: 'Share & Collaborate',
        features: [
            {
                name: 'Share designs',
                free: true,
                plus: true,
            },
            {
                name: 'Collaborate in real-time',
                free: true,
                plus: true,
            },
            {
                name: 'Comment and mention teammates',
                free: true,
                plus: true,
            },
        ],
    },
    {
        category: 'Product Catalog',
        features: [
            {
                name: 'Search product catalog',
                free: true,
                plus: true,
            },
            {
                name: 'View harmonized datasheets',
                free: true,
                plus: true,
            },
            {
                name: 'Shared team product library',
                free: false,
                plus: true,
            },
            {
                name: 'Attach files to products',
                free: false,
                plus: true,
            },
            {
                name: 'AI-enabled product catalog',
                free: 'Up to 5 AI requests',
                plus: 'Unlimited',
                tooltip: 'Ask in-depth questions to the product documentation and get fast answers with references',
            },
        ],
    },
    {
        category: 'Export',
        features: [
            {
                name: 'Save project as image',
                free: 'With watermark',
                plus: 'No watermark',
            },
            {
                name: 'Save project as PDF',
                free: 'With watermark',
                plus: 'No watermark',
            },
        ],
    },
    {
        category: 'Simulations & Analysis',
        features: [
            {
                name: 'Power flow simulations',
                free: `${NEW_USER_CREDIT_AMOUNT} credits to get started`,
                plus: `${CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION]} credits per run per island`,
                highlight: true,
            },
            {
                name: 'Wire sizing',
                free: `${NEW_USER_CREDIT_AMOUNT} credits to get started`,
                plus: `${CreditUsageTypeCosts[CreditUsageType.WIRE_SIZING_SIMULATION]} credits per run`,
                highlight: true,
            },
            {
                name: 'Fault simulations',
                free: `${NEW_USER_CREDIT_AMOUNT} credits to get started`,
                plus: `${CreditUsageTypeCosts[CreditUsageType.FAULT_SIMULATION]} credits per run`,
                highlight: true,
            },
        ],
    },
    {
        category: 'Support',
        features: [
            {
                name: 'DCIDE support',
                free: 'Limited',
                plus: 'Priority',
            },
            {
                name: 'Support from manufacturers',
                free: true,
                plus: true,
            },
            {
                name: 'DC expert support',
                free: false,
                plus: 'Billed per request from credits',
                tooltip:
                    'Get help from a DC expert while designing - billed per request from your credit balance. How-to questions about using DCIDE are always free.',
            },
        ],
    },
] as const;

export const CREDIT_SERVICES: CreditService[] = [
    {
        name: 'Power Flow Simulation',
        creditsPerUse: CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION],
        icon: TbBolt,
        description:
            'Calculate voltage drops, currents and power flows across your DC and AC systems. Charged per island of connected components; time-series runs cost less per row as they grow',
    },
    {
        name: 'Wire Sizing',
        creditsPerUse: CreditUsageTypeCosts[CreditUsageType.WIRE_SIZING_SIMULATION],
        icon: TbLine,
        description: 'Automated conductor sizing based on current ratings and voltage drop requirements',
    },
    {
        name: 'Fault Simulation',
        creditsPerUse: CreditUsageTypeCosts[CreditUsageType.FAULT_SIMULATION],
        icon: TbShieldBolt,
        description: 'Short-circuit and fault current analysis across your entire system',
    },
    {
        name: 'DC Expert Support',
        creditsPerUse: null,
        icon: TbHeadset,
        description:
            'Questions are always free. Get expert help on your DC designing. We provide an estimate before any work begins.',
        sizing: [
            { credits: 0, description: 'Questions about using DCIDE (always free)', unit: 'question' },
            {
                credits: 250,
                description: 'Quick expert question - e.g. fuse selection, cable sizing guidance',
                unit: 'question',
            },
            {
                credits: 1000,
                description: 'Design investigation - e.g. protection coordination review',
                unit: 'review',
            },
            {
                credits: 2000,
                description: 'Detailed analysis - e.g. fault current report for stakeholders',
                unit: 'report',
            },
            {
                credits: 4000,
                description: 'Comprehensive engineering review - e.g. full topology validation',
                unit: 'report',
            },
        ],
    },
];

export type CreditPurchaseOption = {
    type: 'subscription' | 'one-off';
    title: string;
    description: string;
    pricePrefix?: string;
    priceSuffix: string;
    features: string[];
};

export const CREDIT_PURCHASE_OPTIONS: CreditPurchaseOption[] = [
    {
        type: 'subscription',
        title: 'Credit Subscription',
        description: 'Automatically receive credits every month',
        pricePrefix: 'from',
        priceSuffix: 'month',
        features: [
            'Choose your monthly credit amount',
            'Automatically receive credits every month',
            'Credits accumulate across purchases',
            'Credits expire 1 year after purchase',
            'Cancel anytime - no commitment',
            'A Plus subscription is required to purchase credits',
        ],
    },
    {
        type: 'one-off',
        title: 'One-Off Purchase',
        description: 'Buy credits as needed, when you need them',
        priceSuffix: `${DEFAULT_CREDIT_AMOUNT} credits`,
        features: [
            'Buy any amount of credits',
            'Credits accumulate across purchases',
            'Credits expire 1 year after purchase',
            'No monthly commitment',
            'Pay only for what you use',
            'A Plus subscription is required to purchase credits',
        ],
    },
];

export const FAQ_ITEMS = [
    {
        question: 'How does team pricing work?',
        answer: 'The Plus plan is priced per team, not per seat. You get unlimited team members included - invite your entire engineering team without worrying about per-user costs.',
    },
    {
        question: 'What exactly are credits?',
        answer: 'Credits are a flexible unit you can spend on compute-intensive tasks like power flow simulations, fault analysis, wire sizing, and DC expert support. They can be purchased on top of your Plus subscription at any time.',
    },
    {
        question: 'What are the engagement models (Self-Service, Coworker, Full-Service)?',
        answer: "They describe how much engineering support you use - not separate plans. Everyone uses the same Plus subscription and credits. A Solo Installer running their own simulations might use 75 credits a month. An engineering team working with our experts on design reviews might use 750. A full-service partner offloading all DC engineering could use 2,000+. You're always in control of how much you spend.",
    },
    {
        question: 'How do I know how many credits I need?',
        answer: "Use the cost calculator on this page to estimate based on your workflow. If you're already a customer, your upgrade modal will show your actual monthly usage. Most teams start with a modest amount and adjust as they go - credits accumulate, so there's no rush to get it perfect.",
    },
    {
        question: 'Can I use DCIDE for free?',
        answer: "Yes. The Free plan gives you access to the design editor, product catalog, real-time collaboration, and up to 3 designs. You also get 100 credits to try simulations and expert support. To buy additional credits, you'll need to upgrade to Plus.",
    },
    {
        question: 'Do credits expire?',
        answer: 'Yes. Each credit expires one year after its purchase date. Unused credits are never lost when you buy more - they stack on top of your existing balance. If you cancel your subscription, any remaining credits will remain available until your subscription ends at the conclusion of your current billing period.',
    },
    {
        question: 'Can I buy credits without a Plus subscription?',
        answer: 'No. The Free plan includes 100 credits to try simulations and expert support, but additional credits can only be purchased with a Plus subscription.',
    },
    {
        question: 'How does DC expert support work?',
        answer: 'DC expert support gives you direct access to experienced DC engineers who can answer questions, review your designs, and produce documented analysis reports. Questions about using DCIDE are always free. Paid expert support is billed per request, starting at 250 credits for a quick technical question and scaling up to 4,000 credits for a comprehensive engineering review. We always provide an estimate before any work begins, so you know the cost upfront. Credits for support come from the same pool as simulations.',
    },
];
